// What the command-center Agents tab shows about a location's setup for one agent. Kept apart from
// the component so the one easy mistake -- an unanswered setup drawing nothing, which looks exactly
// like a finished one -- is pinned by a test.

/** The fields this decision reads; structurally the onboarding answer the backend sends. */
export interface OnboardingRequirementLike {
  key: string;
  satisfied: boolean;
  detail?: string;
}
export interface OnboardingLike<R extends OnboardingRequirementLike = OnboardingRequirementLike> {
  ready: boolean;
  requirements: readonly R[];
}

export type OnboardingDisplay<R extends OnboardingRequirementLike = OnboardingRequirementLike> =
  | { kind: 'none' }
  /** Assigned, but the backend could not answer (e.g. agent-suite could not list the role's needs). */
  | { kind: 'unknown' }
  | { kind: 'not_ready'; satisfied: number; total: number; missing: R[] };

export function onboardingDisplay<R extends OnboardingRequirementLike>(
  assigned: boolean,
  onboarding: OnboardingLike<R> | null,
): OnboardingDisplay<R> {
  if (!assigned) return { kind: 'none' };
  if (!onboarding) return { kind: 'unknown' };
  if (onboarding.ready) return { kind: 'none' };
  return {
    kind: 'not_ready',
    satisfied: onboarding.requirements.filter((requirement) => requirement.satisfied).length,
    total: onboarding.requirements.length,
    missing: onboarding.requirements.filter((requirement) => !requirement.satisfied),
  };
}

/** The detail lines worth showing even when the requirement is met (e.g. "Google Ads: ready"). */
export function requirementDetails<R extends OnboardingRequirementLike>(
  onboarding: OnboardingLike<R> | null,
): R[] {
  return onboarding ? onboarding.requirements.filter((r) => r.satisfied && !!r.detail) : [];
}

export type RequirementFixer = 'agency' | 'practice';

/** RF S5: who can resolve a missing requirement, in words an operator reads at a glance. */
export function fixableByLabel(fixableBy: readonly RequirementFixer[]): string {
  const agency = fixableBy.includes('agency');
  const practice = fixableBy.includes('practice');
  if (agency && practice) return 'Agency or practice can fix';
  if (practice) return 'Only the practice can fix';
  if (agency) return 'Agency can fix';
  return '';
}
